import { CamelCase } from 'ia-react-core/~/interfaces/CamelCase';
import { BaseTheme } from 'ia-react-core/~/interfaces/BaseTheme';
import COLORS from '../../constants/COLORS';
import FONT_FAMILIES from '../../constants/FONT_FAMILIES';
import LAYOUT from '../../constants/LAYOUT';

import BREAKPOINTS from '../../constants/BREAKPOINTS';

type Colors = Record<CamelCase<keyof typeof COLORS>, string>;
type ColorNames = keyof typeof COLORS;
type CamelColorNames = CamelCase<keyof typeof COLORS>;
const colorNames = Object.keys(COLORS) as ColorNames[];

const baseColors = {
  ...colorNames.reduce((result, colorName: ColorNames) => {
    result[convertToCamelCase(colorName) as CamelColorNames] = COLORS[colorName];
    return result;
  }, {} as Colors),
};

const colors = {
  ...baseColors,
  baseGray: baseColors.gray1,
  smoothGray: baseColors.gray2,
  mediumGray: baseColors.gray3,
  darkGray: baseColors.gray4,
  darkerGray: baseColors.gray5,
  darkestGray: baseColors.gray6,

  lightBlueGray: baseColors.blueGray1,
  baseBlueGray: baseColors.blueGray3,
  darkBlueGray: baseColors.blueGray4,

  baseTurquoise: baseColors.teal4,

  lightPear: baseColors.green3,
  basePear: baseColors.green4,
  darkPear: baseColors.green5,
  darkerPear: baseColors.green6,

  softYellow: baseColors.yellow1,
  softBlue: baseColors.blue12,
  softGreen: baseColors.teal1,
  softPurple: baseColors.purple3,
};

/**
 * @since 0.14.0
 * @visibleName baseTheme
 */
const baseTheme: BaseTheme = {
  breakpoints: BREAKPOINTS,
  colors,

  boxShadows: {
    light: '0 1px 0 0 rgba(0, 0, 0, 0.25)',
    medium: '0 2px 6px 0 rgba(0, 0, 0, 0.06)',
  },

  fontFamily: {
    special: FONT_FAMILIES.MONTSERRAT,
    regular: FONT_FAMILIES.OPEN_SANS,
  },

  fontSize: {
    base: 14,
  },

  layout: {
    headerHeight: 60,
    horizontalLogoHeaderHeight: 60,
    headerPadding: '0 8px',
    footerPadding: '36px 8px',
    lgFooterPadding: '60px 8px',
    minContentWidth: LAYOUT.MIN_CONTENT_WIDTH,
    maxContentWidth: LAYOUT.MAX_CONTENT_WIDTH,
    defaultCol: LAYOUT.DEFAULT_COL,
    defaultColWidth: LAYOUT.DEFAULT_COL_WIDTH,
    minAppHeaderHeight: 36,
    lgMinAppHeaderHeight: 48,
    appHeaderPadding: 8,
    transactionProgressMarginBottom: 8,
    noFooterLanguageButton: true,
  },

  formatters: {
    emptyData: '',
  },

  Button: {
    borderRadius: '3px',
    disabledFontStyle: 'italic',
  },

  PrimaryButton: {
    boxShadow: `0 1px 0 0 ${colors.shadowGray1}`,
    fontWeight: '600',
  },

  ToggleButton: {
    borderRadius: '4px',
  },

  Modal: {
    headerHeight: 48,
    closeButtonBackground: 'rgba(255, 255, 255, .25)',
    closeButtonHoverBackground: 'rgba(255, 255, 255, .5)',
    closeButtonActiveBackground: 'rgba(255, 255, 255, .35)',
    closeButtonRight: 0,
    verticalPadding: 32,
    horizontalPadding: 16,
    horizontalPaddingXs: 16,
    overlayBackground: 'rgba(0, 0, 0, .75)',
    horizontalMarginFromEdgeXs: 0,
    bottomMarginFromEdgeXs: 0,
    closableErrorModal: true,
  },

  Input: {
    disabledFontStyle: 'italic',
    disabledColor: colors.shadowGray2,
    signFocusOffset: 0,
  },

  FormErrorMessage: {
    padding: '12px',
    iconSize: 22,
    iconPaddingRight: 2,
    iconTop: -2,
  },

  Form: {
    formColGutter: 16,
  },

  InlineMessage: {
    borderRadius: '3px',
  },

  Message: {
    fontSize: '16px',
    padding: '16px',
    borderRadius: '3px',
    iconTop: '13px',
    titleMargin: '0 0 8px 0',
    minHeight: `${44 + 32}px`,
    titleTextTransform: 'uppercase',
  },

  RadioButton: {
    disabledBackground: colors.gray1,
  },

  RadioItem: {
    labelTextPaddingLeft: 16,
  },

  Tooltip: { },

  RadioList: {
    padding: '16px 16px 0 16px',
    boxBorderColor: colors.mediumGray,
    shadedBoxBorderColor: 'transparent',
    itemPadding: '0',
    shadedBoxBorderWidth: '0 0 1px 0',
    boxBorderBottomColor: colors.darkGray,
    shadedBoxBorderBottomColor: colors.darkerGray,
  },

  Checkbox: {
    disabledBackground: colors.gray1,
  },

  CheckboxItem: {
    labelTextPaddingLeft: 16,
  },

  CheckboxList: {
    noBox: true,
    padding: '16px 16px 0 16px',
    boxBorderColor: colors.mediumGray,
    shadedBoxBorderColor: 'transparent',
    itemPadding: '0',
    shadedBoxBorderWidth: '0 0 1px 0',
    boxBorderBottomColor: colors.darkGray,
    shadedBoxBorderBottomColor: colors.darkerGray,
  },

  AlertMessage: {
    backgroundColor: colors.yellow3,
  },

  Block: {
    padding: '24px 16px',
    mdPadding: '32px 16px',
  },

  DataTableResourceLoader: {
    tableBottomMargin: 32,
  },

  P: {
    lineHeight: 1.1,
  },

  SideNav: {
    borderBottom: `1px solid ${colors.white}`,
  },

  SideNavLink: {
    background: colors.white,
    borderBottom: `1px solid ${colors.gray3}`,
    hoverBackground: colors.blue12,
    focusBackground: colors.blue1,
    activeBackground: colors.blue14,
    lineHeight: 1.29,
    padding: '15px 16px',
  },

  SideNavCollapse: {
    arrowTop: 0,
    contentPaddingTop: 0,
    contentPaddingBottom: 0,
    contentPaddingLeft: 30,
  },

  Dropdown: {
    border: colors.gray13,
  },
};

function convertToCamelCase<S extends string>(string: string): CamelCase<S> {
  return string
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr: string) => chr.toUpperCase()) as CamelCase<S>;
}

/**
 * @component
 */
export default baseTheme;
